import { useCallback } from "react";
import { useAppSelector, useAppDispatch } from "./redux";
import {
  fetchDashboardDataAction,
  fetchDashboardDataWithPeriodAction,
  fetchViewsTriggersDataAction,
  fetchYearlyViewsTriggersDataAction,
  fetchWeeklyViewsTriggersDataAction,
  fetchDailyViewsTriggersDataAction,
  fetchForeverViewsTriggersDataAction,
  fetchContactsDataAction,
  fetchStatusMeAction,
  fetchViewsSourcesDataAction,
  fetchRatingsDataAction,
} from "../store/dashboardSlice";

export const useDashboard = () => {
  const dispatch = useAppDispatch();
  const {
    status,
    loadingAll,
    overviewLoading,
    monthlyLoading,
    weeklyLoading,
    dailyLoading,
    contactsLoading,
    ratingsLoading,
    errorAll,
    data,
  } = useAppSelector((state) => state.dashboard);

  const refetchData = useCallback(() => {
    dispatch(fetchDashboardDataAction());
  }, [dispatch]);

  const refetchViewsTriggersData = useCallback(() => {
    dispatch(fetchViewsTriggersDataAction());
  }, [dispatch]);

  const refetchYearlyViewsTriggersData = useCallback(() => {
    dispatch(fetchYearlyViewsTriggersDataAction());
  }, [dispatch]);

  const refetchWeeklyViewsTriggersData = useCallback(() => {
    dispatch(fetchWeeklyViewsTriggersDataAction());
  }, [dispatch]);

  const refetchDailyViewsTriggersData = useCallback(() => {
    dispatch(fetchDailyViewsTriggersDataAction());
  }, [dispatch]);

  const refetchForeverViewsTriggersData = useCallback(() => {
    dispatch(fetchForeverViewsTriggersDataAction());
  }, [dispatch]);

  const refetchDashboardDataWithPeriod = useCallback(
    (period: string) => {
      dispatch(fetchDashboardDataWithPeriodAction(period));
    },
    [dispatch]
  );

  const refetchContactsData = useCallback(
    (
      statusId: number,
      fromDate?: string,
      toDate?: string,
      limit?: number,
      offset?: number,
      append?: boolean,
      source?: string,
      query?: string,
      triggerId?: number
    ) => {
      dispatch(
        fetchContactsDataAction({
          statusId,
          fromDate,
          toDate,
          limit,
          offset,
          append,
          source,
          query,
          triggerId,
        })
      );
    },
    [dispatch]
  );

  const refetchStatusMe = useCallback(() => {
    dispatch(fetchStatusMeAction());
  }, [dispatch]);

  const refetchViewsSourcesData = useCallback(() => {
    dispatch(fetchViewsSourcesDataAction());
  }, [dispatch]);

  const refetchRatingsData = useCallback(
    (statusId: number, from?: string, to?: string) => {
      dispatch(fetchRatingsDataAction({ statusId, from, to }));
    },
    [dispatch]
  );

  return {
    // Status info
    status,
    // Loading and error states
    loading: loadingAll,
    overviewLoading,
    monthlyLoading,
    weeklyLoading,
    dailyLoading,
    contactsLoading,
    ratingsLoading,
    error: errorAll,
    // Dashboard data
    overview: data.overview.all,
    overviewMonth: data.overview.month,
    overviewYear: data.overview.year,
    overviewWeek: data.overview.week,
    overviewDay: data.overview.day,
    bySource: data.by_source.all,
    bySourceMonth: data.by_source.month,
    bySourceWeek: data.by_source.week,
    bySourceDay: data.by_source.day,
    triggers: data.by_triggers.all,
    triggersMonth: data.by_triggers.month,
    triggersYear: data.by_triggers.year,
    triggersWeek: data.by_triggers.week,
    triggersDay: data.by_triggers.day,
    triggersForever: data.by_triggers.forever,
    viewsMonth: data.views.month,
    viewsYear: data.views.year,
    viewsWeek: data.views.week,
    viewsDay: data.views.day,
    ratings: data.ratings.all,
    ratingsMonth: data.ratings.month,
    ratingsWeek: data.ratings.week,
    ratingsDay: data.ratings.day,
    ratingsFiltered: data.ratings.filtered,
    contacts: data.contacts,
    contactsPagination: data.contactsPagination,
    monthlySourcesData: data.monthlySourcesData,
    // Actions
    refetchData,
    refetchDashboardDataWithPeriod,
    refetchViewsTriggersData,
    refetchYearlyViewsTriggersData,
    refetchWeeklyViewsTriggersData,
    refetchDailyViewsTriggersData,
    refetchForeverViewsTriggersData,
    refetchContactsData,
    refetchStatusMe,
    refetchViewsSourcesData,
    refetchRatingsData,
    // Helper getters
    isDataLoaded: !!status && !!data.overview.all,
  };
};
